/**
 * src/components/dashboard/AnalyticsModal.tsx
 * Ultra-Premium Apple iOS Audit Reports & Financial Analytics Studio
 * Features: Timeframe filter grid (mobile-fit), month carousel, custom date range,
 * 100% responsive non-overflowing KPI boxes, authentic SVG comparative cashflow chart,
 * category donut chart, top spends list, and compact instant print/export actions.
 */

import React, { useState, useMemo } from 'react';
import { 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  Download, 
  Printer, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Clock, 
  Layers,
  Activity,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { haptic, playTapSound, playSuccessSound } from '../../utils/audioHaptics';
import type { TransactionItem, UserItem, CategoryItem } from '../../services/api';

interface AnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactions: TransactionItem[];
  totalReceived?: number;
  totalPaid?: number;
  users?: UserItem[];
  categories?: CategoryItem[];
}

export const AnalyticsModal: React.FC<AnalyticsModalProps> = ({
  isOpen,
  onClose,
  transactions,
  totalReceived = 0,
  totalPaid = 0,
  users = [],
  categories = []
}) => {
  // Navigation & View Mode
  const [activeTab, setActiveTab] = useState<'cashflow' | 'categories' | 'transactions'>('cashflow');

  // Filter States
  const [timePreset, setTimePreset] = useState<'all' | 'this_month' | 'last_month' | 'custom'>('this_month');
  const [selectedMonth, setSelectedMonth] = useState<string>(''); // YYYY-MM
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('all');

  // Generate list of distinct months present in the transaction history
  const availableMonths = useMemo(() => {
    const monthMap = new Map<string, string>();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Current month
    const now = new Date();
    const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    monthMap.set(curKey, `${monthNames[now.getMonth()]} ${now.getFullYear()}`);

    // Past month
    const past = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const pastKey = `${past.getFullYear()}-${String(past.getMonth() + 1).padStart(2, '0')}`;
    monthMap.set(pastKey, `${monthNames[past.getMonth()]} ${past.getFullYear()}`);

    transactions.forEach(t => {
      if (t.transaction_date) {
        const d = new Date(t.transaction_date);
        if (!isNaN(d.getTime())) {
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
          if (!monthMap.has(key)) {
            monthMap.set(key, label);
          }
        }
      }
    });

    return Array.from(monthMap.entries()).map(([value, label]) => ({ value, label }));
  }, [transactions]);

  // Handle Preset Time Selection
  const handleSelectPreset = (preset: 'all' | 'this_month' | 'last_month' | 'custom') => {
    haptic.tap();
    playTapSound();
    setTimePreset(preset);

    const now = new Date();
    if (preset === 'this_month') {
      const curMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      setSelectedMonth(curMonthKey);
      setCustomStartDate('');
      setCustomEndDate('');
    } else if (preset === 'last_month') {
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthKey = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;
      setSelectedMonth(lastMonthKey);
      setCustomStartDate('');
      setCustomEndDate('');
    } else if (preset === 'all') {
      setSelectedMonth('');
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  // Filtered dataset based on active time, category, and member
  const filteredTransactions = useMemo(() => {
    return transactions.filter(t => {
      // 1. Time / Month Filter
      if (timePreset === 'this_month' || timePreset === 'last_month' || (timePreset !== 'custom' && selectedMonth)) {
        const targetMonth = selectedMonth || (timePreset === 'this_month'
          ? `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
          : '');
        if (targetMonth && t.transaction_date) {
          const tMonth = t.transaction_date.slice(0, 7); // YYYY-MM
          if (tMonth !== targetMonth) return false;
        }
      } else if (timePreset === 'custom') {
        if (customStartDate && t.transaction_date) {
          const s = customStartDate.slice(0, 10);
          if (t.transaction_date.slice(0, 10) < s) return false;
        }
        if (customEndDate && t.transaction_date) {
          const e = customEndDate.slice(0, 10);
          if (t.transaction_date.slice(0, 10) > e) return false;
        }
      }

      // 2. Category Filter
      if (selectedCategory !== 'all') {
        if ((t.category_name || '').toLowerCase() !== selectedCategory.toLowerCase()) {
          return false;
        }
      }

      // 3. Member Filter
      if (selectedMemberId !== 'all') {
        if (String(t.user_id) !== selectedMemberId) {
          return false;
        }
      }

      return true;
    });
  }, [transactions, timePreset, selectedMonth, customStartDate, customEndDate, selectedCategory, selectedMemberId]);

  // Financial Metrics Computed over Filtered Dataset
  const metrics = useMemo(() => {
    let received = 0;
    let paid = 0;
    let maxExpense = 0;

    filteredTransactions.forEach(t => {
      const amt = Number(t.amount) || 0;
      if (t.transaction_type === 'receive') {
        received += amt;
      } else if (t.transaction_type === 'paid') {
        paid += amt;
      }
      if (amt > maxExpense) maxExpense = amt;
    });

    const net = received - paid;
    const txCount = filteredTransactions.length;
    const avgTx = txCount > 0 ? (received + paid) / txCount : 0;
    const totalVolume = received + paid;
    const burnRatio = totalVolume > 0 ? Math.round((paid / totalVolume) * 100) : 0;
    const inflowRatio = totalVolume > 0 ? Math.round((received / totalVolume) * 100) : 0;

    return {
      totalReceived: Math.round(received * 100) / 100,
      totalPaid: Math.round(paid * 100) / 100,
      netDelta: Math.round(net * 100) / 100,
      avgTransaction: Math.round(avgTx * 100) / 100,
      highestExpense: Math.round(maxExpense * 100) / 100,
      count: txCount,
      totalVolume: Math.round(totalVolume * 100) / 100,
      burnRatio,
      inflowRatio
    };
  }, [filteredTransactions]);

  // Category Breakdown Computed over Filtered Dataset
  const categoryStats = useMemo(() => {
    const stats: Record<string, { total: number; count: number; color: string }> = {};
    const palette = [
      '#0a84ff', // iOS Blue
      '#30d158', // iOS Green
      '#ff9f0a', // iOS Orange
      '#bf5af2', // iOS Purple
      '#64d2ff', // iOS Teal
      '#ff375f', // iOS Pink
      '#ffd60a', // iOS Yellow
      '#ac8e68'  // Titanium Gold
    ];

    let colorIdx = 0;
    filteredTransactions.forEach(t => {
      if (t.transaction_type === 'paid') {
        const cat = t.category_name || 'General & Other';
        if (!stats[cat]) {
          stats[cat] = {
            total: 0,
            count: 0,
            color: palette[colorIdx % palette.length]
          };
          colorIdx++;
        }
        stats[cat].total += Number(t.amount) || 0;
        stats[cat].count += 1;
      }
    });

    const list = Object.entries(stats)
      .map(([name, data]) => ({ name, ...data, total: Math.round(data.total * 100) / 100 }))
      .sort((a, b) => b.total - a.total);

    const totalExpenseSum = list.reduce((sum, c) => sum + c.total, 0) || 1;

    // Donut Geometry (Radius = 70, Circumference = 2 * PI * 70 = 439.82)
    const RADIUS = 70;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
    let cumulative = 0;

    const segments = list.slice(0, 6).map(cat => {
      const percent = cat.total / totalExpenseSum;
      const strokeDasharray = `${percent * CIRCUMFERENCE} ${CIRCUMFERENCE}`;
      const strokeDashoffset = -cumulative * CIRCUMFERENCE;
      cumulative += percent;

      return {
        ...cat,
        percent: Math.round(percent * 100),
        strokeDasharray,
        strokeDashoffset
      };
    });

    return { list, segments, totalExpenseSum };
  }, [filteredTransactions]);

  // Cashflow Relative Height Calculations (bounded between 15% and 100% of 130px)
  const maxFlow = Math.max(metrics.totalReceived, metrics.totalPaid, Math.abs(metrics.netDelta), 1);
  const receivedBarHeight = Math.max(16, Math.round((metrics.totalReceived / maxFlow) * 115));
  const paidBarHeight = Math.max(16, Math.round((metrics.totalPaid / maxFlow) * 115));
  const netBarHeight = Math.max(16, Math.round((Math.abs(metrics.netDelta) / maxFlow) * 115));

  // Export Filtered Statement CSV
  const handleExportFilteredCSV = () => {
    haptic.success();
    playSuccessSound();

    if (filteredTransactions.length === 0) {
      alert('No transactions in the selected period to export.');
      return;
    }

    const headers = ['ID', 'Date', 'Type', 'Amount (INR)', 'Category', 'Member', 'Description'];
    const rows = filteredTransactions.map(t => [
      t.id,
      t.transaction_date,
      t.transaction_type.toUpperCase(),
      t.amount,
      `"${(t.category_name || '').replace(/"/g, '""')}"`,
      `"${(t.username || '').replace(/"/g, '""')}"`,
      `"${(t.description || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CKAudit_Report_${timePreset}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Trigger Print Report
  const handlePrintReport = () => {
    haptic.tap();
    playTapSound();
    window.print();
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title="Audit Report & Financial Studio"
      maxWidth="680px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', boxSizing: 'border-box' }}>

        {/* Top Action Dock: Instant App-like Export & Print without deep scrolling */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          paddingBottom: '2px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{
            fontSize: '0.78rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            <Activity size={14} color="var(--ios-blue)" />
            <span>{filteredTransactions.length} transaction{filteredTransactions.length === 1 ? '' : 's'}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button
              type="button"
              onClick={handleExportFilteredCSV}
              className="ios-tap-target"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                height: '30px',
                padding: '0 10px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(118, 118, 128, 0.16)',
                border: 'var(--border-hairline)',
                color: 'var(--text-primary)',
                fontSize: '0.72rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
              title="Download CSV"
            >
              <Download size={12} />
              <span>CSV</span>
            </button>

            <button
              type="button"
              onClick={handlePrintReport}
              className="ios-tap-target"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                height: '30px',
                padding: '0 12px',
                borderRadius: 'var(--radius-pill)',
                background: 'linear-gradient(135deg, var(--ios-blue) 0%, #0066cc 100%)',
                border: 'none',
                color: '#ffffff',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(10, 132, 255, 0.3)'
              }}
              title="Print Statement"
            >
              <Printer size={12} />
              <span>Print / PDF</span>
            </button>
          </div>
        </div>

        {/* 1. Timeframe Filter: 100% Non-Overflowing Grid */}
        <div className="ios-inset-group" style={{ padding: '12px 14px', width: '100%', boxSizing: 'border-box' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Calendar size={14} color="var(--ios-blue)" />
              <span style={{ fontSize: '0.74rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
                Reporting Timeframe
              </span>
            </div>

            {selectedMonth && timePreset !== 'custom' && (
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--ios-blue)' }}>
                {availableMonths.find(m => m.value === selectedMonth)?.label || selectedMonth}
              </span>
            )}
          </div>

          {/* 4-Column Auto-fit Grid (Never overflows mobile screens) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '3px',
            background: 'rgba(118, 118, 128, 0.12)',
            padding: '3px',
            borderRadius: '10px',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <button
              type="button"
              onClick={() => handleSelectPreset('this_month')}
              className={`ios-segment-button ${timePreset === 'this_month' ? 'active' : ''}`}
              style={{
                padding: '6px 2px',
                fontSize: '0.7rem',
                fontWeight: 600,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              This Month
            </button>
            <button
              type="button"
              onClick={() => handleSelectPreset('last_month')}
              className={`ios-segment-button ${timePreset === 'last_month' ? 'active' : ''}`}
              style={{
                padding: '6px 2px',
                fontSize: '0.7rem',
                fontWeight: 600,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Last Month
            </button>
            <button
              type="button"
              onClick={() => handleSelectPreset('all')}
              className={`ios-segment-button ${timePreset === 'all' ? 'active' : ''}`}
              style={{
                padding: '6px 2px',
                fontSize: '0.7rem',
                fontWeight: 600,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              All Time
            </button>
            <button
              type="button"
              onClick={() => handleSelectPreset('custom')}
              className={`ios-segment-button ${timePreset === 'custom' ? 'active' : ''}`}
              style={{
                padding: '6px 2px',
                fontSize: '0.7rem',
                fontWeight: 600,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Custom
            </button>
          </div>

          {/* Month Pills Carousel */}
          {timePreset !== 'custom' && (
            <div style={{
              display: 'flex',
              gap: '6px',
              overflowX: 'auto',
              maxWidth: '100%',
              width: '100%',
              boxSizing: 'border-box',
              paddingTop: '8px',
              paddingBottom: '2px',
              scrollbarWidth: 'none',
              WebkitOverflowScrolling: 'touch'
            }}>
              {availableMonths.map(m => {
                const isSelected = selectedMonth === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => {
                      haptic.tap();
                      playTapSound();
                      setSelectedMonth(m.value);
                    }}
                    style={{
                      flexShrink: 0,
                      padding: '4px 10px',
                      borderRadius: 'var(--radius-pill)',
                      border: isSelected ? '1px solid var(--ios-blue)' : 'var(--border-hairline)',
                      background: isSelected ? 'rgba(10, 132, 255, 0.22)' : 'rgba(118, 118, 128, 0.12)',
                      color: isSelected ? 'var(--ios-blue)' : 'var(--text-secondary)',
                      fontSize: '0.72rem',
                      fontWeight: isSelected ? 700 : 500,
                      cursor: 'pointer'
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Custom Date Range */}
          {timePreset === 'custom' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: '8px',
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: 'var(--border-hairline)'
            }}>
              <div style={{ minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: '3px' }}>
                  From Date
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="glass-input"
                  style={{ height: '34px', fontSize: '0.75rem', padding: '6px 8px' }}
                />
              </div>

              <div style={{ minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: '3px' }}>
                  To Date
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="glass-input"
                  style={{ height: '34px', fontSize: '0.75rem', padding: '6px 8px' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* 2. Secondary Filter Bar: Category & Member Filter */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: users.length > 0 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          gap: '8px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <div style={{ minWidth: 0 }}>
            <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '3px' }}>
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="glass-select"
              style={{ width: '100%', height: '35px', fontSize: '0.78rem', padding: '4px 8px' }}
            >
              <option value="all">All Categories ({transactions.length})</option>
              {categories.map(c => (
                <option key={c.id} value={c.name}>
                  {c.icon || '🏷️'} {c.name}
                </option>
              ))}
            </select>
          </div>

          {users.length > 0 && (
            <div style={{ minWidth: 0 }}>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '3px' }}>
                Member
              </label>
              <select
                value={selectedMemberId}
                onChange={(e) => setSelectedMemberId(e.target.value)}
                className="glass-select"
                style={{ width: '100%', height: '35px', fontSize: '0.78rem', padding: '4px 8px' }}
              >
                <option value="all">All Members</option>
                {users.map(u => (
                  <option key={u.id} value={String(u.id)}>
                    {u.full_name ? `${u.full_name} (@${u.username})` : u.username} {u.is_active === 0 ? '(Deactivated)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* 3. 4 KPI Highlight Tiles: 100% Non-Overflowing Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '8px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Total Received (Inflow) */}
          <div className="ios-inset-group" style={{ padding: '10px 12px', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              <ArrowDownLeft size={13} color="var(--ios-green)" strokeWidth={2.5} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Received</span>
            </div>
            <div className="num-font" style={{
              fontSize: 'clamp(0.92rem, 3.8vw, 1.22rem)',
              fontWeight: 800,
              color: 'var(--ios-green)',
              marginTop: '3px',
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2
            }}>
              +₹{metrics.totalReceived.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.64rem', color: 'var(--text-tertiary)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Credits ({metrics.inflowRatio}%)
            </div>
          </div>

          {/* Total Expenses (Outflow) */}
          <div className="ios-inset-group" style={{ padding: '10px 12px', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              <ArrowUpRight size={13} color="var(--ios-red)" strokeWidth={2.5} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Expenses</span>
            </div>
            <div className="num-font" style={{
              fontSize: 'clamp(0.92rem, 3.8vw, 1.22rem)',
              fontWeight: 800,
              color: 'var(--ios-red)',
              marginTop: '3px',
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2
            }}>
              -₹{metrics.totalPaid.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.64rem', color: 'var(--text-tertiary)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Debits ({metrics.burnRatio}%)
            </div>
          </div>

          {/* Net Cashflow Delta */}
          <div className="ios-inset-group" style={{ padding: '10px 12px', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {metrics.netDelta >= 0 ? <TrendingUp size={13} color="var(--ios-green)" /> : <TrendingDown size={13} color="var(--ios-red)" />}
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Net Position</span>
            </div>
            <div className="num-font" style={{ 
              fontSize: 'clamp(0.92rem, 3.8vw, 1.22rem)', 
              fontWeight: 800, 
              color: metrics.netDelta >= 0 ? 'var(--ios-green)' : 'var(--ios-red)', 
              marginTop: '3px', 
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2
            }}>
              {metrics.netDelta >= 0 ? '+' : ''}₹{metrics.netDelta.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.64rem', color: 'var(--text-tertiary)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {metrics.netDelta >= 0 ? 'Surplus Balance' : 'Deficit Balance'}
            </div>
          </div>

          {/* Avg Velocity */}
          <div className="ios-inset-group" style={{ padding: '10px 12px', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              <Clock size={13} color="var(--ios-blue)" />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Avg / Entry</span>
            </div>
            <div className="num-font" style={{ 
              fontSize: 'clamp(0.92rem, 3.8vw, 1.22rem)', 
              fontWeight: 800, 
              color: 'var(--text-primary)', 
              marginTop: '3px', 
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2
            }}>
              ₹{metrics.avgTransaction.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: '0.64rem', color: 'var(--text-tertiary)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Max: ₹{metrics.highestExpense.toLocaleString('en-IN')}
            </div>
          </div>
        </div>

        {/* 4. Visualization Mode Navigation Capsule */}
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%', boxSizing: 'border-box' }}>
          <div className="ios-segmented-control" style={{ padding: '2px', width: '100%', maxWidth: '380px' }}>
            <button
              type="button"
              onClick={() => {
                haptic.tap();
                playTapSound();
                setActiveTab('cashflow');
              }}
              className={`ios-segment-button ${activeTab === 'cashflow' ? 'active' : ''}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '5px 8px', fontSize: '0.75rem', flex: 1 }}
            >
              <BarChart3 size={13} />
              <span>Cashflow</span>
            </button>
            <button
              type="button"
              onClick={() => {
                haptic.tap();
                playTapSound();
                setActiveTab('categories');
              }}
              className={`ios-segment-button ${activeTab === 'categories' ? 'active' : ''}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '5px 8px', fontSize: '0.75rem', flex: 1 }}
            >
              <PieChart size={13} />
              <span>Categories</span>
            </button>
            <button
              type="button"
              onClick={() => {
                haptic.tap();
                playTapSound();
                setActiveTab('transactions');
              }}
              className={`ios-segment-button ${activeTab === 'transactions' ? 'active' : ''}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '5px 8px', fontSize: '0.75rem', flex: 1 }}
            >
              <Layers size={13} />
              <span>Top Spends</span>
            </button>
          </div>
        </div>

        {/* 5. Tab Content: Pure Vector Visualizations */}
        {activeTab === 'cashflow' && (
          <div className="ios-inset-group" style={{ padding: '14px 14px', width: '100%', boxSizing: 'border-box' }}>
            
            {/* Header with Health Badge */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '6px' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)' }}>
                Cashflow Distribution & Net Flow
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                borderRadius: 'var(--radius-pill)',
                background: metrics.netDelta >= 0 ? 'rgba(48, 209, 88, 0.15)' : 'rgba(255, 69, 58, 0.15)',
                color: metrics.netDelta >= 0 ? 'var(--ios-green)' : 'var(--ios-red)',
                fontSize: '0.68rem',
                fontWeight: 700
              }}>
                {metrics.netDelta >= 0 ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}
                <span>{metrics.netDelta >= 0 ? 'Cashflow Surplus' : 'Cashflow Deficit'}</span>
              </div>
            </div>

            {/* Horizontal Cashflow Split Balance Meter */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                height: '8px',
                borderRadius: 'var(--radius-pill)',
                background: 'rgba(118, 118, 128, 0.16)',
                overflow: 'hidden',
                display: 'flex',
                width: '100%'
              }}>
                <div style={{
                  width: `${metrics.inflowRatio}%`,
                  background: 'linear-gradient(90deg, #30d158 0%, #34c759 100%)',
                  transition: 'width 0.4s ease'
                }} />
                <div style={{
                  width: `${metrics.burnRatio}%`,
                  background: 'linear-gradient(90deg, #ff453a 0%, #ff3b30 100%)',
                  transition: 'width 0.4s ease'
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                <span style={{ color: 'var(--ios-green)', fontWeight: 600 }}>Inflow: {metrics.inflowRatio}%</span>
                <span style={{ color: 'var(--ios-red)', fontWeight: 600 }}>Outflow: {metrics.burnRatio}%</span>
              </div>
            </div>

            {/* 3-Pillar SVG & CSS Comparative Chart with Baseline Grid */}
            <div style={{
              position: 'relative',
              height: '145px',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-around',
              padding: '0 8px 12px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
            }}>
              {/* Background Grid Lines */}
              <div style={{ position: 'absolute', inset: '0 0 12px 0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none', opacity: 0.18 }}>
                <div style={{ borderTop: '1px dashed var(--text-tertiary)', width: '100%' }} />
                <div style={{ borderTop: '1px dashed var(--text-tertiary)', width: '100%' }} />
                <div style={{ borderTop: '1px dashed var(--text-tertiary)', width: '100%' }} />
              </div>

              {/* Pillar 1: Inflow */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', zIndex: 1, flex: 1, maxWidth: '80px' }}>
                <span className="num-font" style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--ios-green)', whiteSpace: 'nowrap' }}>
                  ₹{metrics.totalReceived >= 1000 ? `${(metrics.totalReceived / 1000).toFixed(1)}k` : metrics.totalReceived}
                </span>
                <div style={{
                  width: '100%',
                  maxWidth: '44px',
                  height: `${receivedBarHeight}px`,
                  borderRadius: '8px 8px 3px 3px',
                  background: 'linear-gradient(180deg, #30d158 0%, #15803d 100%)',
                  boxShadow: '0 4px 14px rgba(48, 209, 88, 0.35)',
                  transition: 'height 0.35s ease'
                }} />
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  Inflow
                </span>
              </div>

              {/* Pillar 2: Outflow */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', zIndex: 1, flex: 1, maxWidth: '80px' }}>
                <span className="num-font" style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--ios-red)', whiteSpace: 'nowrap' }}>
                  ₹{metrics.totalPaid >= 1000 ? `${(metrics.totalPaid / 1000).toFixed(1)}k` : metrics.totalPaid}
                </span>
                <div style={{
                  width: '100%',
                  maxWidth: '44px',
                  height: `${paidBarHeight}px`,
                  borderRadius: '8px 8px 3px 3px',
                  background: 'linear-gradient(180deg, #ff453a 0%, #b91c1c 100%)',
                  boxShadow: '0 4px 14px rgba(255, 69, 58, 0.35)',
                  transition: 'height 0.35s ease'
                }} />
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  Outflow
                </span>
              </div>

              {/* Pillar 3: Net Balance Position */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', zIndex: 1, flex: 1, maxWidth: '80px' }}>
                <span className="num-font" style={{
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  color: metrics.netDelta >= 0 ? 'var(--ios-green)' : 'var(--ios-red)',
                  whiteSpace: 'nowrap'
                }}>
                  {metrics.netDelta >= 0 ? '+' : '-'}₹{Math.abs(metrics.netDelta) >= 1000 ? `${(Math.abs(metrics.netDelta) / 1000).toFixed(1)}k` : Math.abs(metrics.netDelta)}
                </span>
                <div style={{
                  width: '100%',
                  maxWidth: '44px',
                  height: `${netBarHeight}px`,
                  borderRadius: '8px 8px 3px 3px',
                  background: metrics.netDelta >= 0
                    ? 'linear-gradient(180deg, #34c759 0%, #059669 100%)'
                    : 'linear-gradient(180deg, #ff3b30 0%, #991b1b 100%)',
                  boxShadow: metrics.netDelta >= 0
                    ? '0 4px 14px rgba(52, 199, 89, 0.35)'
                    : '0 4px 14px rgba(255, 59, 48, 0.35)',
                  transition: 'height 0.35s ease'
                }} />
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  Net Delta
                </span>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: '8px',
              fontSize: '0.72rem',
              color: 'var(--text-tertiary)'
            }}>
              <span>Total Volume: <strong>₹{metrics.totalVolume.toLocaleString('en-IN')}</strong></span>
              <span>Burn Ratio: <strong style={{ color: metrics.burnRatio > 70 ? 'var(--ios-red)' : 'var(--text-primary)' }}>{metrics.burnRatio}%</strong></span>
            </div>
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="ios-inset-group" style={{ padding: '14px 14px', width: '100%', boxSizing: 'border-box' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', marginBottom: '10px', textAlign: 'center' }}>
              Category Expense Allocation
            </div>

            {categoryStats.list.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                No expense entries found in the selected timeframe.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px' }}>
                {/* SVG 360° Donut */}
                <div style={{ position: 'relative', width: '140px', height: '140px' }}>
                  <svg width="140" height="140" viewBox="0 0 160 160" style={{ transform: 'rotate(-90deg)' }}>
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      fill="none"
                      stroke="rgba(118, 118, 128, 0.15)"
                      strokeWidth="14"
                    />
                    {categoryStats.segments.map((seg, i) => (
                      <circle
                        key={i}
                        cx="80"
                        cy="80"
                        r="70"
                        fill="none"
                        stroke={seg.color}
                        strokeWidth="14"
                        strokeDasharray={seg.strokeDasharray}
                        strokeDashoffset={seg.strokeDashoffset}
                        style={{ transition: 'stroke-dasharray 0.4s ease' }}
                      />
                    ))}
                  </svg>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.64rem', color: 'var(--text-tertiary)', fontWeight: 600 }}>Top Spend</div>
                    <div className="num-font" style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                      {categoryStats.segments[0]?.percent || 0}%
                    </div>
                  </div>
                </div>

                {/* Progress bars list for categories */}
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {categoryStats.list.slice(0, 5).map((cat, i) => {
                    const pct = Math.round((cat.total / categoryStats.totalExpenseSum) * 100);
                    return (
                      <div key={i} style={{ width: '100%', minWidth: 0 }}>
                        <div className="flex-between" style={{ fontSize: '0.78rem', marginBottom: '3px' }}>
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {cat.name}
                          </span>
                          <span className="num-font" style={{ fontWeight: 700, color: 'var(--text-secondary)', flexShrink: 0, marginLeft: '6px' }}>
                            ₹{cat.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })} ({pct}%)
                          </span>
                        </div>
                        <div style={{
                          height: '5px',
                          borderRadius: 'var(--radius-pill)',
                          background: 'rgba(118, 118, 128, 0.18)',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${pct}%`,
                            height: '100%',
                            background: cat.color,
                            borderRadius: 'var(--radius-pill)',
                            transition: 'width 0.4s ease'
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="ios-inset-group" style={{ padding: '12px 12px', width: '100%', boxSizing: 'border-box' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
              Largest Transactions in Filtered Window
            </div>

            {filteredTransactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                No transactions match active filter criteria.
              </div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {filteredTransactions
                  .slice()
                  .sort((a, b) => Number(b.amount) - Number(a.amount))
                  .slice(0, 8)
                  .map((t, idx) => (
                    <div
                      key={idx}
                      className="ios-list-row"
                      style={{ padding: '7px 10px', borderRadius: '8px', background: 'rgba(255, 255, 255, 0.03)' }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.description || 'Transaction'}
                        </div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.user_full_name || t.username} • {t.category_name || 'General'} • {t.transaction_date?.slice(0, 10)}
                        </div>
                      </div>
                      <div className="num-font" style={{
                        fontSize: '0.88rem',
                        fontWeight: 800,
                        color: t.transaction_type === 'receive' ? 'var(--ios-green)' : 'var(--ios-red)',
                        flexShrink: 0,
                        marginLeft: '8px'
                      }}>
                        {t.transaction_type === 'receive' ? '+' : '-'}₹{Number(t.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* 6. Compact Bottom Action Dock (Eliminates excessive modal scroll) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '8px',
          marginTop: '2px',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <button
            type="button"
            onClick={handleExportFilteredCSV}
            className="ios-tap-target"
            style={{
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(118, 118, 128, 0.16)',
              border: 'var(--border-hairline)',
              color: 'var(--text-primary)',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <Download size={13} />
            <span>Export CSV</span>
          </button>

          <button
            type="button"
            onClick={handlePrintReport}
            className="ios-tap-target"
            style={{
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              borderRadius: 'var(--radius-sm)',
              background: 'linear-gradient(135deg, var(--ios-blue) 0%, #0066cc 100%)',
              border: 'none',
              color: '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(10, 132, 255, 0.35)'
            }}
          >
            <Printer size={13} />
            <span>Print Statement</span>
          </button>
        </div>

      </div>
    </GlassModal>
  );
};
